<template>
    <div class="glass-theme fade-in-page">
        <b-card :style="{'max-width': '99%', 'width': '99%', 'margin-left': 'auto', 'margin-right': 'auto', 'margin-top': '10px'}" class="glass-card">
            <!-- API Loading Overlay -->
            <transition name="loading-overlay">
                <div v-if="apiLoading" class="loading-overlay">
                    <div class="loading-card">
                        <div class="loading-animation">
                            <div class="loading-ring">
                                <div class="ring"></div>
                                <div class="ring"></div>
                                <div class="ring"></div>
                                <div class="loading-logo">
                                    <i class="fas fa-check-circle"></i>
                                </div>
                            </div>
                        </div>
                        <div class="loading-text">
                            <h3>{{ loadingMessage }}</h3>
                            <div class="loading-dots">
                                <span class="dot"></span>
                                <span class="dot"></span>
                                <span class="dot"></span>
                            </div>
                        </div>
                        <div class="loading-progress">
                            <div class="progress-bar" :style="{ width: loadingProgress + '%' }"></div>
                        </div>
                        <div class="loading-tips loading-tips-fixed">
                            <p class="tip-text">{{ currentTip }}</p>
                        </div>
                    </div>
                </div>
            </transition>

            <b-card-text>
                <h4>DELIVERED ORDERS</h4>
                <div v-if="allItems.length > 0" class="d-flex justify-content-between align-items-center">
                    <span class="text-muted" style="font-size: 14px;">
                        Total Orders: <strong>{{ allItems.length }}</strong>
                        <span v-if="filteredItems.length !== allItems.length" class="text-primary">
                            | Showing: <strong>{{ filteredItems.length }}</strong> ({{ Math.round((filteredItems.length / allItems.length) * 100) }}%)
                        </span>
                    </span>
                </div>
            </b-card-text>

            <div
              v-if="isMobile"
              class="d-flex justify-content-between align-items-center mb-2 py-2"
            >
              <b-button
                size="sm"
                variant="outline-primary"
                @click="filtersVisible = !filtersVisible"
                class="glass-btn-outline"
              >
                <b-icon-funnel-fill class="mr-1" />
                {{ filtersVisible ? "Hide Filters" : "Show Filters" }}
              </b-button>
            </div>

            <b-collapse :visible="!isMobile || filtersVisible" style="width: 100%;">
                <b-card-text class="filters-container">
                    <!-- Date Range Picker -->
                    <div style="display: inline-block; margin: 0px; width: auto; padding: 3px; border-radius: 12px;" class="filter-box">
                        <b-card-text>
                            <div>DATES</div>
                            <b-input id="dates" size="sm" variant="dark" :placeholder="[searchForm.startDate, searchForm.endDate].filter(Boolean).join(' - ') || 'Select Date Range'" style="display: inline-block; width: 220px;" autocomplete="off" class="glass-input"></b-input>
                        </b-card-text>
                    </div>

                    <!-- Search Invoice -->
                    <div style="display: inline-block; margin: 10px; width: auto; padding: 3px; border-radius: 12px;" class="filter-box">
                        <b-card-text>
                            <div>SEARCH INVOICE: </div>
                            <span>
                                <b-form-input 
                                    v-model="searchForm.invoice_id" 
                                    size="sm" 
                                    style="display: inline-block; width: 220px;" 
                                    autocomplete="off" 
                                    class="glass-input" 
                                    placeholder="Enter invoice #" 
                                    @keyup.enter="handleEnterSearch">
                                </b-form-input>
                            </span>
                        </b-card-text>
                    </div>

                    <div style="display: inline-block;">
                        <div>DELIVERY STATUS:</div>
                        <b-form-select v-model="searchForm.delivery_status" :options="deliveryStatusOptionsWithCounts" class="glass-select filter-box" @change="onFilterChange">
                        </b-form-select>
                    </div>

                    <div style="display: inline-block;" v-if="allItems.length > 0">
                        <div>FILTER INVOICES:</div>
                        <b-form-select v-model="searchForm.payment_method" :options="paymentMethodsWithCounts" class="glass-select filter-box" @change="onFilterChange">
                        </b-form-select>
                    </div>

                    <div style="display: inline-block;" v-if="allItems.length > 0">
                        <div>FILTER LOCATIONS:</div>
                        <b-form-select v-model="searchForm.location_id" :options="locationsWithCounts" class="glass-select filter-box" @change="onFilterChange">
                        </b-form-select>
                    </div>

                    <div style="display: inline-block;" v-if="allItems.length > 0">
                        <div>FILTER ORDER TYPE:</div>
                        <b-form-select v-model="searchForm.order_type_filter" :options="orderTypeOptionsWithCounts" class="glass-select filter-box" @change="onFilterChange">
                        </b-form-select>
                    </div>

                    <div style="display: inline-block;" v-if="allItems.length > 0 && hasFinanceOrders">
                        <div>PAYMENT PROCESS:</div>
                        <b-form-select v-model="searchForm.finance_payment_process_filter" :options="financePaymentProcessOptionsWithCounts" class="glass-select filter-box" @change="onFilterChange">
                        </b-form-select>
                    </div>

                    <div style="display: inline-block;" v-if="allItems.length > 0 && hasFinanceOrders">
                        <div>PAYMENT RECEIVED :</div>
                        <b-form-select v-model="searchForm.payment_received_simple_filter" :options="paymentReceivedSimpleOptionsWithCounts" class="glass-select filter-box" @change="onFilterChange">
                        </b-form-select>
                    </div>

                    <div style="margin-left: 20px; display: inline-block;  align-items: center;">
                        <div>&nbsp;</div>
                        <b-button size="sm" variant="primary" @click="applyAllFilters" class="glass-btn-small">
                            APPLY FILTERS & SEARCH
                        </b-button>
                        <b-button size="sm" variant="secondary" @click="resetToDefaultFilter" class="glass-btn-small ml-2">
                            RESET TO NEW
                        </b-button>
                    </div>
                </b-card-text>
            </b-collapse>

            <div>
                <div v-if="filteredItems.length > 0" style="margin-top: 10px; overflow-x: auto;">
                    <table class="text-monospace glass-table" style="width: 100%; margin-top: 10px;">
                        <thead>
                            <tr style="background: linear-gradient(135deg, #1e293b, #0f172a); color: white;">
                                <th class="ipt_tdhr" @click="sortby('invoice_id')" style="min-width: 130px;">
                                    INVOICE # <span class="sort-icon">↕</span>
                                </th>
                                <th class="ipt_tdhr" @click="sortby('customer')" style="min-width: 170px;">
                                    CUSTOMER <span class="sort-icon">↕</span>
                                </th>
                                <th class="ipt_tdhr" @click="sortby('state')">
                                    STATUS <span class="sort-icon">↕</span>
                                </th>
                                <th class="ipt_tdhr" @click="sortby('order_types_display')">
                                    ORDER TYPE <span class="sort-icon">↕</span>
                                </th>
                                <th class="ipt_tdhr" @click="sortby('zipcode')">
                                    ZIPCODE <span class="sort-icon">↕</span>
                                </th>
                                <th class="ipt_tdhr" @click="sortby('source_locsub')" style="min-width: 150px;">
                                    SOLD FROM <span class="sort-icon">↕</span>
                                </th>
                                <th class="ipt_tdhr" @click="sortby('nordered')">
                                    # ITEMS ORDERED <span class="sort-icon">↕</span>
                                </th>
                                <th class="ipt_tdhr" @click="sortby('ndelivered')">
                                    # ITEMS DELIVERED <span class="sort-icon">↕</span>
                                </th>
                                <th class="ipt_tdhr" @click="sortby('all_delivered')" style="width: 70px;">
                                    ALL DELIVERED <span class="sort-icon">↕</span>
                                </th>
                                <th class="ipt_tdhr" @click="sortby('delivery_dates')">
                                    DELIVERY DATES <span class="sort-icon">↕</span>
                                </th>
                                <th class="ipt_tdhr" style="min-width: 400px;">
                                    PAYMENT METHODS
                                </th>
                                <th class="ipt_tdhr" @click="sortby('finance_status')" v-if="hasFinanceOrders">
                                    FINANCE PAYMENT PROCESS <span class="sort-icon">↕</span>
                                </th>
                                <th class="ipt_tdhr" @click="sortby('deposit_status')" v-if="hasCashOrCreditOrders">
                                   FINANCE PAYMENT RECIEVED <span class="sort-icon">↕</span>
                                </th>
                                <th class="ipt_tdhr">
                                    ACTION
                                </th>
                            </tr>
                        </thead>

                        <tbody>
                            <tr v-for="(item, idx) in filteredItems" 
                                :key="item.invoice_id + '_' + item.finance_status + '_' + item.deposit_status" 
                                class="fade-in-row" 
                                :class="{'discrepancy-row': hasDiscrepancy(item)}"
                                :style="{ animationDelay: `${idx * 0.02}s` }">
                                <td class="ipt_tdr">
                                    <span class="cell-invoice">{{ item.invoice_id }}</span>
                                </td>
                                <td class="ipt_tdr" style="width: 250px;">
                                    <div class="customer-info">
                                        <strong>{{ item.fullname }}</strong>
                                        <span class="customer-phone">#{{ item.phone }}</span>
                                        <span class="customer-email">@ {{ item.email }}</span>
                                    </div>
                                </td>
                                <td class="ipt_tdr">
                                    <b-badge :variant="getStateVariant(item.state)" class="state-badge">
                                        {{ item.state }}
                                    </b-badge>
                                </td>
                                <td class="ipt_tdr">
                                    <div v-if="item.order_types_display">
                                        {{ item.order_types_display }}
                                    </div>
                                    <div v-else style="color: #999;">
                                        N/A
                                    </div>
                                </td>
                                <td class="ipt_tdr">
                                    {{ item.zipcode }}
                                </td>
                                <td class="ipt_tdr">
                                    {{ getLocationName(item.source_locsub) }}
                                </td>
                                <td class="ipt_tdr" :style="{'width': '100px', 'background-color': get_bg_color(item)}">
                                    {{ item.nordered }}
                                </td>
                                <td class="ipt_tdr" :style="{'width': '100px', 'background-color': get_bg_color(item)}">
                                    {{ item.ndelivered }}
                                </td>
                                <td class="ipt_tdr" style="width: 100px;">
                                    <b-badge :variant="getAllDeliveredBadgeVariant(item)">
                                        {{ getAllDeliveredStatus(item) }}
                                    </b-badge>
                                </td>
                                <td class="ipt_tdr" style="width: 100px;">
                                    <div v-for="(ddate, ddidx) in (item.delivery_dates || [])" :key="ddidx" class="delivery-date-badge">
                                        {{ ddate }}
                                    </div>
                                </td>
                                <td class="ipt_tdr">
                                    <div v-for="(pm, pmidx) in (item.payment_methods || [])" :key="pmidx" :style="{'background-color': get_pm_color(pm.value), 'padding': '8px', 'border-radius': '8px', 'margin': '5px', 'text-align': 'left', 'font-size': '13px'}">
                                        <div>
                                            <div v-if="pm.value == 'cash'">
                                                <strong>TYPE:</strong> {{ safeUpper(pm.value) }}
                                            </div>
                                            <div v-else>
                                                <strong>TYPE:</strong> {{ safeUpper(pm.value) }} - {{ safeUpper(pm.name) }}
                                            </div>
                                        </div>
                                        
                                        <div style="display: flex; gap: 12px; align-items: center; margin-top: 4px;">
                                            <div>
                                                <strong>AMOUNT:</strong> ${{ toMoney(pm.amount) }}
                                            </div>
                                            
                                            <div class="mt-1">
                                                <strong>ACTUAL AMOUNT:</strong>
                                                <b-form-input 
                                                    type="number" 
                                                    step="0.01"
                                                    :value="getActualAmount(pm)"
                                                    @input="(val) => updateActualAmount(item, pm, pmidx, val)"
                                                    size="sm"
                                                    style="width: 70px; display: inline-block; margin-top: -4px;"
                                                    class="glass-input">
                                                </b-form-input>
                                            </div>
                                        </div>
                                        
                                        <div>
                                            <strong>SHORT:</strong> 
                                            <span :style="{'color': getShortColor(pm), 'font-weight': 'bold', 'font-size': '14px'}">
                                                ${{ calculateShort(pm).toFixed(2) }}
                                            </span>
                                        </div>

                                        <template v-if="pm.value == 'finance'">
                                            <div class="mt-1">
                                                <date-picker v-model="pm.fund_date" format="YYYY-MM-DD" valueType="format" class="glass-datepicker-small" placeholder="FUND DATE" @change="update_pm_fund_date(item)" :disabled="is_action_disabled()"></date-picker>
                                            </div>
                                        </template>
                                    </div>
                                </td>
                                <td class="ipt_tdr" style="text-align: center;" v-if="hasFinanceOrders">
                                    <b-form-checkbox 
                                        :checked="item.finance_status"
                                        @change="(val) => update_finance_status_boolean(item, val)"
                                        :disabled="is_action_disabled() || item.completed"
                                        class="mb-2">
                                        {{ item.finance_status ? 'Processed' : 'Processed' }}
                                    </b-form-checkbox>
                                 </td>
                                <td class="ipt_tdr" style="text-align: center;" v-if="hasCashOrCreditOrders">
                                    <b-form-checkbox 
                                        :checked="item.deposit_status"
                                        @change="(val) => update_deposit_status_boolean(item, val)"
                                        :disabled="is_action_disabled() || item.completed"
                                        class="mb-2">
                                        {{ item.deposit_status ? 'Received' : 'Received' }}
                                    </b-form-checkbox>
                                 </td>
                                <td class="ipt_tdr" style="width: 100px; text-align: center;">
                                    <div class="d-flex flex-column gap-2">
                                        <span v-if="item.completed">
                                            <b-button size="sm" variant="secondary" @click="change_invoice_completed_status(item)" v-if="isAdmin()" :disabled="is_action_disabled()" class="glass-btn-small">
                                                UNDO
                                            </b-button>
                                        </span>
                                        <span v-else>
                                            <b-button size="sm" variant="success" @click="change_invoice_completed_status(item)" :disabled="check_complete_allowed(item) == false || is_action_disabled()" class="">COMPLETE</b-button>
                                        </span>
                                    </div>
                                 </td>
                             </tr>
                        </tbody>
                        
                        <tfoot v-if="filteredItems.length > 0">
                            <tr style="background: linear-gradient(135deg, #1e293b, #0f172a); color: white; font-weight: bold;">
                                <td colspan="10" class="text-right" style="padding: 12px;">
                                    TOTAL SHORT AMOUNT:
                                 </td>
                                <td class="text-left" :style="{'color': getTotalShortColor(), 'font-size': '16px', 'font-weight': 'bold', 'padding': '12px'}">
                                    ${{ calculateTotalShort().toFixed(2) }}
                                 </td>
                                <td colspan="3"></td>
                             </tr>
                        </tfoot>
                     </table>
                </div>
                <div v-else>
                    <b-alert show variant="warning" class="glass-alert">No records</b-alert>
                </div>
            </div>
        </b-card>

        <!-- Dispute Modal -->
        <b-modal 
            v-model="showDisputeModal" 
            title="Mark Order as Disputed" 
            size="lg"
            @ok="submitDispute"
            @hidden="resetDisputeForm"
            ok-title="Submit Dispute"
            cancel-title="Cancel"
            class="glass-modal">
            <b-form>
                <b-form-group label="Invoice ID">
                    <b-form-input v-model="disputeForm.invoice_id" readonly class="glass-input"></b-form-input>
                </b-form-group>

                <b-form-group label="Dispute Type" label-for="dispute_type">
                    <b-form-select 
                        id="dispute_type"
                        v-model="disputeForm.dispute_type" 
                        :options="disputeTypeOptions" 
                        required
                        :state="disputeForm.dispute_type ? true : null"
                        class="glass-select">
                        <template #first>
                            <b-form-select-option :value="null" disabled>-- Select Dispute Type --</b-form-select-option>
                        </template>
                    </b-form-select>
                    <b-form-invalid-feedback :force-show="true" v-if="!disputeForm.dispute_type && formSubmitted">
                        Please select a dispute type
                    </b-form-invalid-feedback>
                </b-form-group>

                <b-form-group label="Customer Statement" label-for="customer_statement">
                    <b-form-radio-group 
                        id="customer_statement"
                        v-model="disputeForm.customer_statement" 
                        required>
                        <b-form-radio value="received">Customer has received the merchandise</b-form-radio>
                        <b-form-radio value="not_received">Customer has NOT received the merchandise</b-form-radio>
                    </b-form-radio-group>
                    <b-form-invalid-feedback :force-show="true" v-if="!disputeForm.customer_statement && formSubmitted">
                        Please select customer statement
                    </b-form-invalid-feedback>
                </b-form-group>

                <b-form-group label="Issue Type" label-for="issue_type">
                    <b-form-select 
                        id="issue_type"
                        v-model="disputeForm.issue_type" 
                        :options="issueTypeOptions" 
                        required
                        :state="disputeForm.issue_type ? true : null"
                        class="glass-select">
                        <template #first>
                            <b-form-select-option :value="null" disabled>-- Select Issue Type --</b-form-select-option>
                        </template>
                    </b-form-select>
                    <b-form-invalid-feedback :force-show="true" v-if="!disputeForm.issue_type && formSubmitted">
                        Please select an issue type
                    </b-form-invalid-feedback>
                </b-form-group>

                <b-form-group label="Dispute Resolution" label-for="resolution">
                    <b-form-select 
                        id="resolution"
                        v-model="disputeForm.resolution" 
                        :options="resolutionOptions" 
                        required
                        :state="disputeForm.resolution ? true : null"
                        class="glass-select">
                        <template #first>
                            <b-form-select-option :value="null" disabled>-- Select Resolution --</b-form-select-option>
                        </template>
                    </b-form-select>
                    <b-form-invalid-feedback :force-show="true" v-if="!disputeForm.resolution && formSubmitted">
                        Please select a resolution
                    </b-form-invalid-feedback>
                </b-form-group>

                <b-form-group label="Did we lose the money?" label-for="money_lost">
                    <b-form-radio-group 
                        id="money_lost"
                        v-model="disputeForm.money_lost" 
                        required>
                        <b-form-radio :value="true">Yes, we lost the money</b-form-radio>
                        <b-form-radio :value="false">No, we did not lose the money</b-form-radio>
                    </b-form-radio-group>
                    <b-form-invalid-feedback :force-show="true" v-if="disputeForm.money_lost === null && formSubmitted">
                        Please select if money was lost
                    </b-form-invalid-feedback>
                </b-form-group>

                <b-form-group label="Dispute Details" label-for="details">
                    <b-form-textarea 
                        id="details"
                        v-model="disputeForm.details" 
                        rows="3" 
                        placeholder="Provide additional details about the dispute..."
                        class="glass-input">
                    </b-form-textarea>
                </b-form-group>

                <b-form-group label="Upload Supporting Image" label-for="image">
                    <b-form-file 
                        id="image"
                        v-model="disputeForm.image" 
                        accept="image/*" 
                        placeholder="Choose an image or drop it here..."
                        drop-placeholder="Drop image here..."
                        class="glass-input">
                    </b-form-file>
                    <div v-if="disputeForm.image_preview" class="mt-2">
                        <img :src="disputeForm.image_preview" style="max-width: 200px; max-height: 200px;" class="img-thumbnail">
                    </div>
                </b-form-group>
            </b-form>
        </b-modal>
    </div>
</template>

<script>
    import flatPickr from "vue-flatpickr-component";
    import "flatpickr/dist/flatpickr.css";

    import Loading from 'vue-loading-overlay';
    import 'vue-loading-overlay/dist/vue-loading.css';

    import moment from "moment"
    import global_vars from "@/utilities/global_vars"
    import Backer from "@/components/globals/Backer"
    import Requestor from "@/utilities/Requestor"

    import DatePicker from 'vue2-datepicker';
    import 'vue2-datepicker/index.css';
    import { BIconFunnelFill } from "bootstrap-vue";

    import Lightpick from 'lightpick';
    import 'lightpick/css/lightpick.css';

    // ============================================================
    // STATE CONSTANTS (mirror the backend SQL)
    // ============================================================
    const EXCLUDED_STATES = [-8, -7, 32, -34, -33, 33];
    const PARTIAL_DELIVERED_STATES = [0, 2, 3, 30, 9, 10, 11, 13, 4, 5, 6, 12];
    const DELIVERED_STATE = 1;

    // ============================================================
    // SAFE HELPERS (module scope, no `this`)
    // ============================================================
    function safeString(val) {
        if (val === null || val === undefined) return '';
        if (typeof val === 'string') return val;
        if (typeof val === 'number' || typeof val === 'boolean') return String(val);
        if (Array.isArray(val)) return val.map(safeString).join(', ');
        if (typeof val === 'object') {
            try { return JSON.stringify(val); } catch (e) { return ''; }
        }
        try { return String(val); } catch (e) { return ''; }
    }

    function safeUpper(val) {
        return safeString(val).toUpperCase();
    }

    function safeLower(val) {
        return safeString(val).toLowerCase();
    }

    function safeNumber(val, fallback = 0) {
        const n = parseFloat(val);
        return Number.isFinite(n) ? n : fallback;
    }

    function safeMoney(val) {
        return safeNumber(val, 0).toFixed(2);
    }

    function normalizePaymentMethod(pm) {
        if (!pm || typeof pm !== 'object') {
            return {
                value: '',
                name: '',
                amount: 0,
                actual_amount: null,
                fund_date: null,
                processed: null,
                received: null
            };
        }
        return {
            ...pm,
            value: pm.value !== null && pm.value !== undefined ? String(pm.value) : '',
            name: pm.name !== null && pm.name !== undefined ? safeString(pm.name) : '',
            amount: pm.amount !== null && pm.amount !== undefined ? pm.amount : 0,
            actual_amount: pm.actual_amount !== null && pm.actual_amount !== undefined ? pm.actual_amount : null,
            fund_date: pm.fund_date !== undefined ? pm.fund_date : null,
            processed: pm.processed !== undefined ? pm.processed : null,
            received: pm.received !== undefined ? pm.received : null
        };
    }

    export default {
        name: "AdminToolsDeliveredOrders",
        components: {Backer, flatPickr, Loading, DatePicker},
        data: function() {
            return {
                packet: { userDetails: {}, locsubsdict: {}, locations: [] },
                sort_asc: true,
                // master (unfiltered) list — used to build filter option counts
                allItems: [],
                // filtered list — actually rendered in the table
                items: [],
                items_being_loaded: false,
                years: [...Array(10).keys()].map(function(d){
                    return {value: 2021+(d+1), text:2021+(d+1) }
                }),
                year: null,
                month: null,
                payment_methods: [
                    {value: 'cash', text:"CASH INVOLVED"},
                    {value: 'cc', text:"CC INVOLVED"},
                    {value: 'finance', text:"FINANCE INVOLVED"},
                    {value: 'store_credit', text:"STORE CREDIT INVOLVED"},
                ],
                payment_methods_colors: {
                    "cash": "#d4edda",
                    "cc": "#e2e3e5",
                    "finance": "#d1ecf1",
                    "store_credit": "#fff3cd"
                },
                delivery_statuses: [
                    {value: "all", text: "ALL"},
                    {value: "not_delivered", text: "NOT DELIVERED"},
                    {value: "partial", text: "PARTIAL"},
                    {value: "partial_delivered_item", text: "PARTIAL DELIVERED ITEM"},
                    {value: "delivered", text: "DELIVERED"},
                    {value: "completed", text: "COMPLETED"},
                ],
                truck: null,
                location_id: null,
                payment_method: null,
                search_query: null,
                chunksize: 200,
                chunkoptions: [
                    {value: 100, text: "100 RECORDS"},
                    {value: 200, text: "200 RECORDS"},
                    {value: 500, text: "500 RECORDS"},
                    {value: 1000, text: "1000 RECORDS"},
                    {value: null, text: "ALL RECORDS"},
                ],
                chunk_allowed: 200,
                order_type_filter: null,
                orderTypeOptions: [],
                searchForm: {
                    startDate: null,
                    endDate: null,
                    invoice_id: null,
                    delivery_status: "all",
                    payment_method: null,
                    location_id: null,
                    order_type_filter: null,
                    finance_payment_process_filter: "false",
                    payment_received_simple_filter: null
                },
                activeFilters: {
                    startDate: null,
                    endDate: null,
                    invoice_id: null,
                    delivery_status: "all",
                    payment_method: null,
                    location_id: null,
                    order_type_filter: null,
                    finance_payment_process_filter: "false",
                    payment_received_simple_filter: null
                },
                filtersVisible: false,
                isMobile: false,
                formSubmitted: false,
                showDisputeModal: false,
                disputeForm: {
                    invoice_id: null,
                    dispute_type: null,
                    customer_statement: null,
                    issue_type: null,
                    resolution: null,
                    money_lost: null,
                    details: null,
                    image: null,
                    image_preview: null
                },
                disputeTypeOptions: [
                    {value: "customer", text: "🏠 Customer Dispute"},
                    {value: "credit_card", text: "💳 Credit Card Company Dispute"},
                    {value: "snap_finance", text: "🏦 Snap Finance Dispute"},
                    {value: "merchant_service", text: "💼 Merchant Service Dispute"},
                    {value: "attorney", text: "⚖️ Attorney Dispute"},
                    {value: "court", text: "🏛️ Court Dispute"}
                ],
                issueTypeOptions: [
                    {value: "fraud_charges", text: "Fraud Charges"},
                    {value: "broken_merchandise", text: "Broken Merchandise"},
                    {value: "wrong_item", text: "Wrong Item Received"},
                    {value: "missing_item", text: "Missing Item"},
                    {value: "not_as_described", text: "Not as Described"},
                    {value: "delivery_issue", text: "Delivery Issue"},
                    {value: "other", text: "Other"}
                ],
                resolutionOptions: [
                    {value: "customer_favor", text: "Resolved in Customer's Favor"},
                    {value: "business_favor", text: "Resolved in Business's Favor"},
                    {value: "partial_refund", text: "Partial Refund Issued"},
                    {value: "full_refund", text: "Full Refund Issued"},
                    {value: "replacement", text: "Replacement Sent"},
                    {value: "pending", text: "Pending Resolution"}
                ],
                apiLoading: false,
                loadingMessage: 'Loading Delivered Orders',
                loadingProgress: 0,
                currentTipIndex: 0,
                loadingInterval: null,
                tipInterval: null,
                msgInterval: null,
                apiCallStack: 0,
                autoSaveTimeout: null,
                tips: [
                    "📦 Loading delivered orders...",
                    "🚚 Fetching delivery data...",
                    "💰 Processing payment info...",
                    "📊 Calculating statistics...",
                    "🔄 Syncing with server...",
                    "✨ Organizing order list..."
                ],
                datePickerInstance: null,
                filterDebounceTimer: null,
                searchMode: false,
                PARTIAL_DELIVERED_STATES
            }
        },
        computed: {
            currentTip() {
                return this.tips[this.currentTipIndex] || this.tips[0];
            },

            hasFinanceOrders: function() {
                return this.allItems.some(item => 
                    Array.isArray(item.payment_methods) && 
                    item.payment_methods.some(pm => pm.value === 'finance')
                );
            },
            hasCashOrCreditOrders: function() {
                return this.allItems.some(item => 
                    Array.isArray(item.payment_methods) && 
                    item.payment_methods.some(pm => pm.value === 'cash' || pm.value === 'cc')
                );
            },

            deliveryStatusOptionsWithCounts() {
                const counts = {
                    all: this.allItems.length,
                    not_delivered: 0,
                    partial: 0,
                    partial_delivered_item: 0,
                    delivered: 0,
                    completed: 0
                };
                
                this.allItems.forEach(item => {
                    const status = safeLower(this.getAllDeliveredStatus(item));
                    if (status === 'no') {
                        counts.not_delivered++;
                    } else if (status === 'partial') {
                        counts.partial++;
                    } else if (status === 'partial delivered') {
                        counts.partial_delivered_item++;
                    } else if (status === 'yes') {
                        counts.delivered++;
                    }
                    if (item.completed) {
                        counts.completed++;
                    }
                });
                
                return [
                    { value: "all", text: `ALL (${counts.all})` },
                    { value: "not_delivered", text: `NOT DELIVERED (${counts.not_delivered})` },
                    { value: "partial", text: `PARTIAL (${counts.partial})` },
                    { value: "partial_delivered_item", text: `PARTIAL DELIVERED ITEM (${counts.partial_delivered_item})` },
                    { value: "delivered", text: `DELIVERED (${counts.delivered})` },
                    { value: "completed", text: `COMPLETED (${counts.completed})` }
                ];
            },
            
            financePaymentProcessOptionsWithCounts() {
                let newCount = 0;
                let processedCount = 0;
                let discrepancyCount = 0;
                let totalFinance = 0;
                
                this.allItems.forEach(item => {
                    const pms = Array.isArray(item.payment_methods) ? item.payment_methods : [];
                    const hasFinancePayment = pms.some(pm => pm.value === 'finance');
                    
                    if (hasFinancePayment) {
                        totalFinance++;
                        
                        const hasDiscrepancy = pms.some(pm => {
                            if (pm.value !== 'finance') return false;
                            const expectedAmount = safeNumber(pm.amount, 0);
                            const actualAmount = (pm.actual_amount !== undefined && pm.actual_amount !== null) 
                                ? safeNumber(pm.actual_amount, 0)
                                : expectedAmount;
                            return actualAmount < expectedAmount;
                        });
                        
                        if (hasDiscrepancy) {
                            discrepancyCount++;
                        } else if (item.finance_status === true) {
                            processedCount++;
                        } else {
                            newCount++;
                        }
                    }
                });
                
                const options = [];
                
                if (totalFinance > 0) {
                    options.push({ value: null, text: `ALL (${totalFinance})` });
                }
                
                options.push({ value: "false", text: `New (${newCount})` });
                options.push({ value: "true", text: `Processed (${processedCount})` });
                
                if (discrepancyCount > 0) {
                    options.push({ value: "discrepancy", text: `Discrepancy (${discrepancyCount})` });
                }
                
                if (totalFinance === 0) {
                    return [
                        { value: null, text: 'No Finance Orders', disabled: true }
                    ];
                }
                
                return options;
            },
            
            paymentReceivedSimpleOptionsWithCounts() {
                let receivedCount = 0;
                let notReceivedCount = 0;
                
                this.allItems.forEach(item => {
                    const pms = Array.isArray(item.payment_methods) ? item.payment_methods : [];
                    const hasFinancePayment = pms.some(pm => pm.value === 'finance');
                    
                    if (hasFinancePayment) {
                        const financePayments = pms.filter(pm => pm.value === 'finance');
                        const allReceived = financePayments.every(pm => pm.payment_received === true);
                        if (allReceived) {
                            receivedCount++;
                        } else {
                            notReceivedCount++;
                        }
                    }
                });
                
                const options = [];
                const totalFinance = receivedCount + notReceivedCount;
                
                if (totalFinance > 0) {
                    options.push({ value: null, text: `ALL (${totalFinance})` });
                    options.push({ value: "true", text: `Received (${receivedCount})` });
                    options.push({ value: "false", text: `Not Received (${notReceivedCount})` });
                } else {
                    options.push({ value: null, text: 'No Finance Orders', disabled: true });
                }
                
                return options;
            },
            
            paymentMethodsWithCounts() {
                const counts = {
                    cash: 0,
                    cc: 0,
                    finance: 0,
                    store_credit: 0
                };
                
                const invoiceTracker = {
                    cash: new Set(),
                    cc: new Set(),
                    finance: new Set(),
                    store_credit: new Set()
                };
                
                this.allItems.forEach(item => {
                    const pms = Array.isArray(item.payment_methods) ? item.payment_methods : [];
                    pms.forEach(pm => {
                        if (counts.hasOwnProperty(pm.value)) {
                            invoiceTracker[pm.value].add(item.invoice_id);
                        }
                    });
                });
                
                for (const [key, set] of Object.entries(invoiceTracker)) {
                    counts[key] = set.size;
                }
                
                const options = [
                    { value: null, text: `ALL (${this.allItems.length})` }
                ];
                
                for (const [key, count] of Object.entries(counts)) {
                    if (count > 0) {
                        options.push({ 
                            value: key, 
                            text: `${safeUpper(key)} INVOLVED (${count})` 
                        });
                    }
                }
                
                return options;
            },
            
            locationsWithCounts() {
                const locationCounts = {};
                this.allItems.forEach(item => {
                    const locationName = this.getLocationName(item.source_locsub);
                    locationCounts[locationName] = (locationCounts[locationName] || 0) + 1;
                });
                
                const options = [
                    { value: null, text: `ALL (${this.allItems.length})` }
                ];
                
                const sortedLocations = Object.entries(locationCounts)
                    .sort((a, b) => b[1] - a[1]);
                
                for (const [name, count] of sortedLocations) {
                    options.push({ 
                        value: name, 
                        text: `${name} (${count})` 
                    });
                }
                
                return options;
            },
            
            orderTypeOptionsWithCounts() {
                const orderTypeCounts = {};
                this.allItems.forEach(item => {
                    if (Array.isArray(item.order_types) && item.order_types.length) {
                        item.order_types.forEach(type => {
                            if (type) {
                                orderTypeCounts[type] = (orderTypeCounts[type] || 0) + 1;
                            }
                        });
                    }
                });
                
                const options = [
                    { value: null, text: `ALL (${this.allItems.length})` }
                ];
                
                const sortedTypes = Object.entries(orderTypeCounts)
                    .sort((a, b) => b[1] - a[1]);
                
                for (const [type, count] of sortedTypes) {
                    options.push({ 
                        value: type, 
                        text: `${safeUpper(type)} (${count})` 
                    });
                }
                
                return options;
            },
            
            filteredItems: function() {
                let filtered = [...this.allItems];
                
                const isSearchingByInvoice = this.searchForm.invoice_id && String(this.searchForm.invoice_id).trim() !== '';
                const isSearchingByDeliveryStatus = this.searchForm.delivery_status && this.searchForm.delivery_status !== 'all';
                this.searchMode = isSearchingByInvoice || isSearchingByDeliveryStatus;
                
                if (this.searchForm.payment_method) {
                    filtered = filtered.filter(item => 
                        Array.isArray(item.payment_methods) && 
                        item.payment_methods.some(pm => pm.value === this.searchForm.payment_method)
                    );
                }
                
                if (this.searchForm.location_id) {
                    filtered = filtered.filter(item => {
                        const locationName = this.getLocationName(item.source_locsub);
                        return locationName === this.searchForm.location_id;
                    });
                }
                
                if (this.searchForm.order_type_filter) {
                    filtered = filtered.filter(item => 
                        Array.isArray(item.order_types) && 
                        item.order_types.includes(this.searchForm.order_type_filter)
                    );
                }
                
                if (this.searchForm.finance_payment_process_filter && !this.searchMode) {
                    if (this.searchForm.finance_payment_process_filter === 'discrepancy') {
                        filtered = filtered.filter(item => {
                            const pms = Array.isArray(item.payment_methods) ? item.payment_methods : [];
                            return pms.some(pm => {
                                const expectedAmount = safeNumber(pm.amount, 0);
                                const actualAmount = (pm.actual_amount !== undefined && pm.actual_amount !== null) 
                                    ? safeNumber(pm.actual_amount, 0)
                                    : expectedAmount;
                                return actualAmount < expectedAmount;
                            });
                        });
                    } else if (this.searchForm.finance_payment_process_filter === 'true') {
                        filtered = filtered.filter(item => {
                            const pms = Array.isArray(item.payment_methods) ? item.payment_methods : [];
                            if (pms.length === 0) return false;
                            return pms.every(pm => {
                                if (pm.value === 'finance') {
                                    return item.finance_status === true;
                                }
                                if (pm.value === 'cash' || pm.value === 'cc') {
                                    if (pm.processed !== undefined && pm.processed !== null) {
                                        return pm.processed === true;
                                    }
                                    const expectedAmount = safeNumber(pm.amount, 0);
                                    const actualAmount = (pm.actual_amount !== undefined && pm.actual_amount !== null) 
                                        ? safeNumber(pm.actual_amount, 0)
                                        : expectedAmount;
                                    return actualAmount >= expectedAmount;
                                }
                                return true;
                            });
                        });
                    } else {
                        const filterValue = this.searchForm.finance_payment_process_filter === 'true';
                        filtered = filtered.filter(item => {
                            const pms = Array.isArray(item.payment_methods) ? item.payment_methods : [];
                            const hasFinancePayment = pms.some(pm => pm.value === 'finance');
                            
                            if (!hasFinancePayment) {
                                if (pms.length === 0) return false;
                                return pms.some(pm => {
                                    if (pm.value === 'cash' || pm.value === 'cc') {
                                        if (pm.processed !== undefined && pm.processed !== null) {
                                            return pm.processed !== true;
                                        }
                                        const expectedAmount = safeNumber(pm.amount, 0);
                                        const actualAmount = (pm.actual_amount !== undefined && pm.actual_amount !== null) 
                                            ? safeNumber(pm.actual_amount, 0)
                                            : expectedAmount;
                                        return actualAmount < expectedAmount;
                                    }
                                    return false;
                                });
                            }
                            
                            return item.finance_status === filterValue;
                        });
                    }
                }
                
                if (this.searchForm.payment_received_simple_filter && !this.searchMode) {
                    const filterValue = this.searchForm.payment_received_simple_filter === 'true';
                    filtered = filtered.filter(item => {
                        const pms = Array.isArray(item.payment_methods) ? item.payment_methods : [];
                        if (pms.length === 0) return false;
                        
                        return pms.every(pm => {
                            if (pm.value === 'finance') {
                                return item.deposit_status === filterValue;
                            }
                            if (pm.value === 'cash' || pm.value === 'cc') {
                                if (pm.received !== undefined && pm.received !== null) {
                                    return pm.received === filterValue;
                                }
                                const expectedAmount = safeNumber(pm.amount, 0);
                                const actualAmount = (pm.actual_amount !== undefined && pm.actual_amount !== null) 
                                    ? safeNumber(pm.actual_amount, 0)
                                    : expectedAmount;
                                return actualAmount >= expectedAmount;
                            }
                            return true;
                        });
                    });
                }
                
                filtered = filtered.filter(item => this.filterByDateRange(item));
                
                if (this.searchForm.invoice_id) {
                    const searchTerm = safeLower(this.searchForm.invoice_id).trim();
                    filtered = filtered.filter(item => 
                        safeLower(item.invoice_id).includes(searchTerm)
                    );
                }
                
                if (this.searchForm.delivery_status && this.searchForm.delivery_status !== 'all') {
                    filtered = filtered.filter(item => {
                        const status = safeLower(this.getAllDeliveredStatus(item));
                        if (this.searchForm.delivery_status === 'delivered') {
                            return status === 'yes';
                        } else if (this.searchForm.delivery_status === 'partial') {
                            return status === 'partial';
                        } else if (this.searchForm.delivery_status === 'partial_delivered_item') {
                            return status === 'partial delivered';
                        } else if (this.searchForm.delivery_status === 'not_delivered') {
                            return status === 'no';
                        } else if (this.searchForm.delivery_status === 'completed') {
                            return item.completed === true;
                        }
                        return true;
                    });
                }
                
                return filtered;
            },
            
            totalShortAmount: function() {
                let total = 0;
                this.filteredItems.forEach(item => {
                    const pms = Array.isArray(item.payment_methods) ? item.payment_methods : [];
                    pms.forEach(pm => {
                        total += this.calculateShort(pm);
                    });
                });
                return total;
            }
        },
        watch: {
            searchForm: {
                handler() {
                    this.activeFilters = {
                        startDate: this.searchForm.startDate,
                        endDate: this.searchForm.endDate,
                        invoice_id: this.searchForm.invoice_id,
                        delivery_status: this.searchForm.delivery_status || "all",
                        payment_method: this.searchForm.payment_method,
                        location_id: this.searchForm.location_id,
                        order_type_filter: this.searchForm.order_type_filter,
                        finance_payment_process_filter: this.searchForm.finance_payment_process_filter || "false",
                        payment_received_simple_filter: this.searchForm.payment_received_simple_filter
                    };
                },
                deep: true
            },
            allItems: {
                handler() {
                    this.$forceUpdate();
                },
                deep: true
            }
        },
        methods: {
            // ========== SAFE HELPERS (also exposed to template) ==========
            safeString,
            safeUpper,
            safeLower,

            toMoney(val) {
                return safeMoney(val);
            },

            getLocationName(locsub) {
                const dict = this.packet && this.packet.locsubsdict ? this.packet.locsubsdict : {};
                if (locsub === null || locsub === undefined) return 'Unknown';
                const name = dict[locsub];
                return (name === null || name === undefined || name === '') ? 'Unknown' : String(name);
            },

            isAdmin() {
                return !!(this.packet && this.packet.userDetails && this.packet.userDetails.userrole === 'admin');
            },

            handleEnterSearch() {
                this.applyAllFilters();
                if (document.activeElement && document.activeElement.blur) {
                    document.activeElement.blur();
                }
            },

            getActualAmount: function(pm) {
                if (!pm) return 0;
                if (pm.actual_amount !== undefined && pm.actual_amount !== null) {
                    return pm.actual_amount;
                }
                return pm.amount !== undefined && pm.amount !== null ? pm.amount : 0;
            },

            calculateShort: function(pm) {
                if (!pm) return 0;
                const expectedAmount = safeNumber(pm.amount, 0);
                const actualAmount = (pm.actual_amount !== undefined && pm.actual_amount !== null) 
                    ? safeNumber(pm.actual_amount, 0)
                    : expectedAmount;
                return expectedAmount - actualAmount;
            },

            calculateTotalShort: function() {
                return this.totalShortAmount;
            },

            getShortColor: function(pm) {
                const short = this.calculateShort(pm);
                if (short > 0) return '#dc3545';
                if (short < 0) return '#28a745';
                return '#6c757d';
            },

            getTotalShortColor: function() {
                const total = this.totalShortAmount;
                if (total > 0) return '#dc3545';
                if (total < 0) return '#28a745';
                return '#ffffff';
            },

            hasDiscrepancy: function(item) {
                const pms = Array.isArray(item.payment_methods) ? item.payment_methods : [];
                return pms.some(pm => {
                    const expectedAmount = safeNumber(pm.amount, 0);
                    const actualAmount = (pm.actual_amount !== undefined && pm.actual_amount !== null) 
                        ? safeNumber(pm.actual_amount, 0)
                        : expectedAmount;
                    return actualAmount < expectedAmount;
                });
            },

            updateActualAmount: function(item, pm, pmidx, value) {
                const actualValue = safeNumber(value, 0);
                this.$set(pm, 'actual_amount', actualValue);
                if (this.autoSaveTimeout) clearTimeout(this.autoSaveTimeout);
                this.autoSaveTimeout = setTimeout(() => {
                    this.savePaymentMethod(item);
                }, 1000);
            },

            checkAmount: function(item, pm) {
                const expectedAmount = safeNumber(pm && pm.amount, 0);
                const actualAmount = pm && pm.actual_amount !== undefined && pm.actual_amount !== null 
                    ? safeNumber(pm.actual_amount, 0) 
                    : expectedAmount;
                const shortAmount = expectedAmount - actualAmount;
                
                let toastTitle = "";
                let toastVariant = "";
                
                if (shortAmount > 0) {
                    toastTitle = `⚠️ Short Amount Detected: $${shortAmount.toFixed(2)}`;
                    toastVariant = "warning";
                } else if (shortAmount < 0) {
                    toastTitle = `✅ Overpayment: $${Math.abs(shortAmount).toFixed(2)}`;
                    toastVariant = "info";
                } else {
                    toastTitle = "✓ Exact Amount Received";
                    toastVariant = "success";
                }
                
                this.$bvToast.toast(
                    `Expected: $${expectedAmount.toFixed(2)} | Actual: $${actualAmount.toFixed(2)} | Short: $${shortAmount.toFixed(2)}`,
                    {
                        title: toastTitle,
                        autoHideDelay: 3000,
                        variant: toastVariant
                    }
                );
                
                this.savePaymentMethod(item);
            },

            savePaymentMethod: function(item) {
                let that = this;
                let path = "/api/sales/update_payment_method/";
                let data = {
                    invoice_id: item.invoice_id,
                    payment_methods: item.payment_methods
                };
                
                let s = function(response) {
                    if (response && response.data && response.data.error) {
                        that.$bvToast.toast(
                            response.data.error,
                            {title: "Error", autoHideDelay: 1500, variant: "warning"}
                        );
                    } else if (response && response.data && response.data.result) {
                        that.$bvToast.toast(
                            "Payment method updated successfully.",
                            {title: "Success", autoHideDelay: 1500, variant: "success"}
                        );
                    }
                };
                
                let f = function(error) {
                    console.error('Error updating payment method:', error);
                    that.$bvToast.toast(
                        "Error updating payment method. Please try again.",
                        {title: "Error", autoHideDelay: 1500, variant: "danger"}
                    );
                };
                
                Requestor.postAsyncP(path, data, this, s, f);
            },
            
            onFilterChange() {
                this.activeFilters = {
                    startDate: this.searchForm.startDate,
                    endDate: this.searchForm.endDate,
                    invoice_id: this.searchForm.invoice_id,
                    delivery_status: this.searchForm.delivery_status || "all",
                    payment_method: this.searchForm.payment_method,
                    location_id: this.searchForm.location_id,
                    order_type_filter: this.searchForm.order_type_filter,
                    finance_payment_process_filter: this.searchForm.finance_payment_process_filter || "false",
                    payment_received_simple_filter: this.searchForm.payment_received_simple_filter
                };
            },
            
            applyAllFilters: function() {
                if (this.searchForm.startDate && this.searchForm.endDate) {
                    let diff = Math.abs(moment(this.searchForm.endDate).diff(moment(this.searchForm.startDate), 'days'));
                    if (diff > 365) {
                        this.$bvToast.toast(
                            "Cannot search more than 365 days at once.",
                            {title: "Error", autoHideDelay: 1500, variant: "warning"}
                        );
                        return;
                    }
                }
                
                const isSearchingByInvoice = this.searchForm.invoice_id && String(this.searchForm.invoice_id).trim() !== '';
                const isSearchingByDeliveryStatus = this.searchForm.delivery_status && this.searchForm.delivery_status !== 'all';
                this.searchMode = isSearchingByInvoice || isSearchingByDeliveryStatus;
                
                this.activeFilters = {
                    startDate: this.searchForm.startDate,
                    endDate: this.searchForm.endDate,
                    invoice_id: this.searchForm.invoice_id,
                    delivery_status: this.searchForm.delivery_status || "all",
                    payment_method: this.searchForm.payment_method,
                    location_id: this.searchForm.location_id,
                    order_type_filter: this.searchForm.order_type_filter,
                    finance_payment_process_filter: this.searchMode ? null : this.searchForm.finance_payment_process_filter || "false",
                    payment_received_simple_filter: this.searchMode ? null : this.searchForm.payment_received_simple_filter
                };
                
                this.get_all_items_delivered_invoices();
                
                this.$bvToast.toast(
                    "Filters and search applied successfully",
                    {title: "Success", autoHideDelay: 1500, variant: "success"}
                );
            },
            
            resetToDefaultFilter: function() {
                this.searchMode = false;
                
                this.searchForm = {
                    startDate: null,
                    endDate: null,
                    invoice_id: null,
                    delivery_status: "all",
                    payment_method: null,
                    location_id: null,
                    order_type_filter: null,
                    finance_payment_process_filter: "false",
                    payment_received_simple_filter: null
                };
                
                this.activeFilters = {
                    startDate: null,
                    endDate: null,
                    invoice_id: null,
                    delivery_status: "all",
                    payment_method: null,
                    location_id: null,
                    order_type_filter: null,
                    finance_payment_process_filter: "false",
                    payment_received_simple_filter: null
                };
                
                if (this.datePickerInstance) {
                    this.datePickerInstance.setDateRange(null, null);
                    const el = document.getElementById('dates');
                    if (el) el.value = '';
                }
                
                this.get_all_items_delivered_invoices();
                
                this.$bvToast.toast(
                    "Reset to default filter (showing only NEW finance orders)",
                    {title: "Reset", autoHideDelay: 1500, variant: "info"}
                );
            },
            
            filterByDateRange: function(item) {
                if (!this.searchForm.startDate && !this.searchForm.endDate) {
                    return true;
                }
                try {
                    if (!item.ts) return true;
                    const itemDate = moment(item.ts);
                    if (!itemDate.isValid()) return true;
                    let startDate = this.searchForm.startDate ? moment(this.searchForm.startDate) : null;
                    let endDate = this.searchForm.endDate ? moment(this.searchForm.endDate) : null;
                    if (startDate && !startDate.isValid()) startDate = null;
                    if (endDate && !endDate.isValid()) endDate = null;
                    if (startDate && endDate) {
                        return itemDate.isBetween(startDate, endDate, 'day', '[]');
                    } else if (startDate) {
                        return itemDate.isSameOrAfter(startDate, 'day');
                    } else if (endDate) {
                        return itemDate.isSameOrBefore(endDate, 'day');
                    }
                    return true;
                } catch (e) {
                    console.error('Error filtering by date:', e);
                    return true;
                }
            },
            
            startApiLoading(message = 'Loading Delivered Orders') {
                this.apiCallStack++;
                if (!this.apiLoading) {
                    this.startLoadingAnimation(message);
                } else {
                    this.loadingMessage = message;
                }
            },

            stopApiLoading() {
                this.apiCallStack--;
                if (this.apiCallStack <= 0 && this.apiLoading) {
                    this.stopLoadingAnimation(true);
                }
            },

            startLoadingAnimation(message = 'Loading Delivered Orders') {
                this.apiLoading = true;
                this.loadingMessage = message;
                this.loadingProgress = 0;
                this.currentTipIndex = 0;
                
                this.loadingInterval = setInterval(() => {
                    if (this.loadingProgress < 90) {
                        this.loadingProgress += Math.random() * 10;
                        if (this.loadingProgress > 90) this.loadingProgress = 90;
                    }
                }, 300);
                
                this.tipInterval = setInterval(() => {
                    if (this.currentTipIndex < this.tips.length - 1) {
                        this.currentTipIndex++;
                    }
                }, 2000);
                
                let messages = ['Loading Delivered Orders', 'Fetching Data', 'Processing Orders', 'Updating Status', 'Finalizing'];
                let msgIndex = 0;
                this.msgInterval = setInterval(() => {
                    if (msgIndex < messages.length - 1) {
                        msgIndex++;
                        this.loadingMessage = messages[msgIndex];
                    }
                }, 1800);
            },

            stopLoadingAnimation(success = true) {
                if (this.loadingInterval) clearInterval(this.loadingInterval);
                if (this.tipInterval) clearInterval(this.tipInterval);
                if (this.msgInterval) clearInterval(this.msgInterval);
                this.loadingInterval = null;
                this.tipInterval = null;
                this.msgInterval = null;
                
                if (success) {
                    this.loadingProgress = 100;
                    setTimeout(() => {
                        this.apiLoading = false;
                    }, 500);
                } else {
                    this.apiLoading = false;
                }
            },
            
            getStateVariant(state) {
                const stateStr = safeLower(state);
                if (stateStr === 'delivered') return 'success';
                if (stateStr === 'partial') return 'warning';
                if (stateStr === 'pending') return 'info';
                return 'secondary';
            },
            
            handleResize() {
                this.isMobile = window.innerWidth < 768;
            },

            resetDisputeForm() {
                this.disputeForm = {
                    invoice_id: null,
                    dispute_type: null,
                    customer_statement: null,
                    issue_type: null,
                    resolution: null,
                    money_lost: null,
                    details: null,
                    image: null,
                    image_preview: null
                };
                this.formSubmitted = false;
            },

            update_finance_status_boolean: function(item, newValue) {
                let that = this;
                const originalValue = item.finance_status;
                this.$set(item, 'finance_status', newValue);
                
                let data = {
                    invoice_id: item.invoice_id,
                    finance_status: newValue
                };
                let path = "/api/sales/update_finance_status_boolean/";
                let s = function(response){
                    if (response && response.data && response.data.error){
                        that.$bvToast.toast(response.data.error, {title: "Error", autoHideDelay: 1500, variant: "warning"});
                        that.$set(item, 'finance_status', originalValue);
                    } else {
                        that.$bvToast.toast("Finance status updated.", {title: "Success", autoHideDelay: 1500, variant: "success"});
                    }
                };
                let f = function(error){
                    console.error('Error updating finance status:', error);
                    that.$bvToast.toast("Error updating finance status.", {title: "Error", autoHideDelay: 1500, variant: "danger"});
                    that.$set(item, 'finance_status', originalValue);
                };
                Requestor.postAsyncP(path, data, this, s, f);
            },

            update_deposit_status_boolean: function(item, newValue) {
                let that = this;
                const originalValue = item.deposit_status;
                this.$set(item, 'deposit_status', newValue);
                
                let data = {
                    invoice_id: item.invoice_id,
                    deposit_status: newValue
                };
                let path = "/api/sales/update_deposit_status_boolean/";
                let s = function(response){
                    if (response && response.data && response.data.error){
                        that.$bvToast.toast(response.data.error, {title: "Error", autoHideDelay: 1500, variant: "warning"});
                        that.$set(item, 'deposit_status', originalValue);
                    } else {
                        that.$bvToast.toast("Deposit status updated.", {title: "Success", autoHideDelay: 1500, variant: "success"});
                    }
                };
                let f = function(error){
                    console.error('Error updating deposit status:', error);
                    that.$bvToast.toast("Error updating deposit status.", {title: "Error", autoHideDelay: 1500, variant: "danger"});
                    that.$set(item, 'deposit_status', originalValue);
                };
                Requestor.postAsyncP(path, data, this, s, f);
            },

            updateOrderTypeOptions: function() {
                const orderTypes = new Set();
                this.allItems.forEach(item => {
                    if (Array.isArray(item.order_types) && item.order_types.length) {
                        item.order_types.forEach(type => {
                            if (type) orderTypes.add(type);
                        });
                    }
                });
                this.orderTypeOptions = Array.from(orderTypes).map(type => ({
                    value: type,
                    text: safeUpper(type)
                })).sort((a, b) => a.text.localeCompare(b.text));
            },

            getAllDeliveredStatus: function(item) {
                if (!item) return 'No';
                const nordered   = safeNumber(item.nordered, 0);
                const ndelivered = safeNumber(item.ndelivered, 0);
                const itemStates = Array.isArray(item.item_states) ? item.item_states : [];

                if (nordered === 0) {
                    return 'Yes';
                }

                const hasPartialDeliveredItem = itemStates.some(s => 
                    PARTIAL_DELIVERED_STATES.includes(Number(s))
                );

                if (ndelivered === 0) {
                    return hasPartialDeliveredItem ? 'Partial Delivered' : 'No';
                }

                if (ndelivered < nordered) {
                    return hasPartialDeliveredItem ? 'Partial Delivered' : 'Partial';
                }

                return 'Yes';
            },

            getAllDeliveredBadgeVariant: function(item) {
                const s = this.getAllDeliveredStatus(item);
                if (s === 'Yes') return 'success';
                if (s === 'Partial') return 'warning';
                if (s === 'Partial Delivered') return 'info';
                return 'secondary';
            },

            chunk_n: function(items){
                if (this.chunkoptions.length > 0) {
                    this.chunkoptions[this.chunkoptions.length -1].value = Math.max(0, (items ? items.length : 0) - 1);
                }
            },

            get_bg_color: function(item){
                if (item && Number(item.nordered) !== Number(item.ndelivered) && Number(item.ndelivered) > 0){
                    return '#fff3cd'
                }
                return '#f8f9fa'
            },

            is_action_disabled: function(e){
                let status = false;
                if (e && e.preventDefault){
                    e.preventDefault();
                }
                let userrole = this.packet && this.packet.userDetails ? this.packet.userDetails.userrole : null;
                if (['locationadmin', 'salesperson', 'warehouseuser', 'warehouseman'].includes(userrole)){
                    status = true
                }
                return status
            },

            check_complete_allowed: function(item){
                if (!item) return false;
                let status = true;
                const pms = Array.isArray(item.payment_methods) ? item.payment_methods : [];
                const financePms = pms.filter(d => d.value === 'finance');
                if (financePms.length > 0) {
                    let fund_dates = financePms.map(d => d.fund_date);
                 
                    if ((fund_dates.includes(null) || fund_dates.includes(NaN) || fund_dates.includes(undefined)) || 
                        item.finance_status == false || item.deposit_status == false ||
                        (item.nordered != item.ndelivered)) {
                        status = false;
                    }
                    else {
                        status = true;
                    }
                }
                else {
                    if (item.nordered != item.ndelivered){
                        status = false;
                    }
                    else {
                        status = true;
                    }
                }
                return status
            },

            update_pm_fund_date: function(item){
                let that = this;
                let path = "/api/sales/update_payment_method/"
                let data = item;
                let s = function(response){
                    if (response && response.data && response.data.error){
                        that.$bvToast.toast(
                            response.data.error,
                            {title: "Error", autoHideDelay: 1500, variant:"warning"}
                        );
                    }
                    else {
                        that.$bvToast.toast(
                            "Successfully updated fund date.",
                            {title: "Success", autoHideDelay: 1500, variant:"success"}
                        );
                    }
                }
                let f = function(){
                    that.$bvToast.toast(
                        "Error updating fund date.",
                        {title: "Error", autoHideDelay: 1500, variant:"warning"}
                    );
                }
                Requestor.postAsyncP(path, data, this, s, f);
            },

            get_pm_color: function(pmval){
                const key = safeString(pmval);
                return this.payment_methods_colors[key] || '#f8f9fa';
            },

            reset: function(){
                this.month = null;
            },

            change_invoice_completed_status: function(item){
                let data = {invoice_id: item.invoice_id, status: false}
                if (item.completed == true){
                    data.status = false
                }
                else {
                    data.status = true;
                }
                let that = this;
                let path = "/api/sales/change_invoice_completed_status/"
                let s = function(response){
                    if (response && response.data && response.data.error){
                        that.$bvToast.toast(
                            response.data.error,
                            {title: "Error", autoHideDelay: 1500, variant:"warning"}
                        );
                    }
                    else {
                        that.$bvToast.toast(
                            "Successfully updated invoice.",
                            {title: "Success", autoHideDelay: 1500, variant:"success"}
                        );
                        item.completed = data.status;
                    }
                    that.items_being_loaded = false;
                }
                let f = function(){
                    that.$bvToast.toast(
                        "Error updating invoice.",
                        {title: "Error", autoHideDelay: 1500, variant:"warning"}
                    );
                    that.items_being_loaded = false
                }
                this.items_being_loaded = true;
                Requestor.postAsyncP(path, data, this, s, f);
            },

            get_all_items_delivered_invoices: function(){
                this.startApiLoading('Loading Delivered Orders');
                let data = {};
                
                if (this.activeFilters.startDate || this.activeFilters.endDate) {
                    data.startDate = this.activeFilters.startDate;
                    data.endDate = this.activeFilters.endDate;
                }
                else if (this.year && this.month) {
                    data.year = this.year;
                    data.month = this.month;
                }
                else if (this.activeFilters.invoice_id) {
                    data.invoice_id = this.activeFilters.invoice_id;
                }
                
                if (this.packet && this.packet.userDetails && this.packet.userDetails.properties && this.packet.userDetails.properties.location_id){
                    data.location_id = this.packet.userDetails.properties.location_id;
                }
                
                if (this.activeFilters.delivery_status && this.activeFilters.delivery_status !== 'all') {
                    data.delivery_status = this.activeFilters.delivery_status;
                }
                
                if (this.activeFilters.finance_payment_process_filter && !this.searchMode) {
                    data.finance_payment_process_filter = this.activeFilters.finance_payment_process_filter;
                }
                
                if (this.activeFilters.payment_received_simple_filter && !this.searchMode) {
                    data.payment_received_filter = this.activeFilters.payment_received_simple_filter;
                }
                
                let that = this;
                let path = "/api/sales/get_all_items_delivered_invoices/"
                let s = function(response){
                    if (response && response.data && response.data.error){
                        that.$bvToast.toast(
                            response.data.error,
                            {title: "Error", autoHideDelay: 1500, variant:"warning"}
                        );
                    }
                    else {
                        if (response && response.data && response.data.result && Array.isArray(response.data.result)) {
                            // ============================================================
                            // NORMALIZE THE API RESPONSE — every field the template
                            // touches gets a safe default & correct type here, so
                            // no inline .toUpperCase()/.toLowerCase() ever crashes.
                            // ============================================================
                            const normalized = response.data.result.map(rawItem => {
                                const item = rawItem && typeof rawItem === 'object' ? rawItem : {};

                                // --- top-level string-ish fields ---
                                item.invoice_id = item.invoice_id !== null && item.invoice_id !== undefined
                                    ? item.invoice_id
                                    : '';
                                item.fullname   = item.fullname   !== null && item.fullname   !== undefined ? String(item.fullname)   : '';
                                item.phone      = item.phone      !== null && item.phone      !== undefined ? String(item.phone)      : '';
                                item.email      = item.email      !== null && item.email      !== undefined ? String(item.email)      : '';
                                item.zipcode    = item.zipcode    !== null && item.zipcode    !== undefined ? String(item.zipcode)    : '';
                                item.state      = item.state      !== null && item.state      !== undefined ? String(item.state)      : '';

                                // --- numeric-ish fields ---
                                item.nordered   = safeNumber(item.nordered, 0);
                                item.ndelivered = safeNumber(item.ndelivered, 0);

                                // --- booleans ---
                                item.completed      = !!item.completed;
                                item.finance_status = item.finance_status === true;
                                item.deposit_status = item.deposit_status === true;

                                // --- arrays ---
                                item.item_states = Array.isArray(item.item_states) ? item.item_states : [];
                                item.order_types = Array.isArray(item.order_types) ? item.order_types : [];
                                item.delivery_dates = Array.isArray(item.delivery_dates)
                                    ? item.delivery_dates.map(d => d === null || d === undefined ? '' : String(d))
                                    : [];

                                // --- order_types_display: string or null ---
                                if (item.order_types_display === null || item.order_types_display === undefined) {
                                    item.order_types_display = null;
                                } else {
                                    item.order_types_display = String(item.order_types_display);
                                }

                                // --- payment_methods: normalize each entry ---
                                item.payment_methods = Array.isArray(item.payment_methods)
                                    ? item.payment_methods.map(normalizePaymentMethod)
                                    : [];

                                return item;
                            });

                            that.allItems = normalized;
                            that.items = normalized;
                            that.chunk_n(that.allItems);
                            that.updateOrderTypeOptions();
                        } else {
                            that.allItems = [];
                            that.items = [];
                        }
                    }
                    that.items_being_loaded = false;
                    that.stopApiLoading();
                }
                let f = function(error){
                    console.error('Error loading delivered orders:', error);
                    that.$bvToast.toast(
                        "Error loading delivered orders. Please try again later.",
                        {title: "Error", autoHideDelay: 1500, variant:"danger"}
                    );
                    that.items_being_loaded = false;
                    that.stopApiLoading();
                }
                this.items_being_loaded = true;
                that.allItems = [];
                that.items = [];
                Requestor.getSync(path, data, s, f);
            },

            sortby: function(key){
                if (this.sort_asc){
                    this.allItems = Requestor.sort(this.allItems, key)
                }
                else {
                    this.allItems = Requestor.sort_reverse(this.allItems, key)
                }
                this.sort_asc = !this.sort_asc;
            },

            openDisputeModal: function(item) {
                this.disputeForm = {
                    invoice_id: item.invoice_id,
                    dispute_type: null,
                    customer_statement: null,
                    issue_type: null,
                    resolution: null,
                    money_lost: null,
                    details: null,
                    image: null,
                    image_preview: null
                };
                this.formSubmitted = false;
                this.showDisputeModal = true;
            },

            submitDispute: function(bvModalEvt) {
                bvModalEvt.preventDefault();
                this.formSubmitted = true;
                if (!this.disputeForm.dispute_type || 
                    !this.disputeForm.customer_statement || 
                    !this.disputeForm.issue_type || 
                    !this.disputeForm.resolution || 
                    this.disputeForm.money_lost === null) {
                    this.$bvToast.toast("Please fill in all required fields including Dispute Type.", 
                        {title: "Validation Error", autoHideDelay: 3000, variant: "warning"});
                    return;
                }
                let formData = new FormData();
                formData.append("invoice_id", this.disputeForm.invoice_id);
                formData.append("dispute_type", this.disputeForm.dispute_type);
                formData.append("customer_statement", this.disputeForm.customer_statement);
                formData.append("issue_type", this.disputeForm.issue_type);
                formData.append("resolution", this.disputeForm.resolution);
                formData.append("money_lost", this.disputeForm.money_lost);
                formData.append("details", this.disputeForm.details || "");
                if (this.disputeForm.image) {
                    formData.append("image", this.disputeForm.image);
                }
                let that = this;
                let path = "/api/sales/create_dispute/";
                let s = function(response){
                    if (response && response.data && response.data.error){
                        that.$bvToast.toast(response.data.error, 
                            {title: "Error", autoHideDelay: 1500, variant: "warning"});
                    } else {
                        that.$bvToast.toast("Dispute submitted successfully.", 
                            {title: "Success", autoHideDelay: 1500, variant: "success"});
                        that.showDisputeModal = false;
                        that.resetDisputeForm();
                        const item = that.allItems.find(i => i.invoice_id === that.disputeForm.invoice_id);
                        if (item) {
                            item.finance_status = false;
                            item.deposit_status = false;
                        }
                    }
                };
                let f = function(error){
                    console.error('Error submitting dispute:', error);
                    that.$bvToast.toast("Error submitting dispute. Please try again.", 
                        {title: "Error", autoHideDelay: 1500, variant: "danger"});
                };
                Requestor.postAsyncP(path, formData, this, s, f);
            }
        },

        created: function() {
            try {
                this.packet = JSON.parse(localStorage.packet) || {};
            } catch (e) {
                this.packet = {};
            }
            try {
                this.packet.userDetails = JSON.parse(localStorage.userDetails) || {};
            } catch (e) {
                this.packet.userDetails = {};
            }
            if (!this.packet.locsubsdict) this.packet.locsubsdict = {};
            if (!Array.isArray(this.packet.locations)) this.packet.locations = [];
            this.packet.locations = this.packet.locations.sort(function(a,b) {
                return (a.is_main_warehouse || 0) - (b.is_main_warehouse || 0);
            });
            
            this.searchForm.finance_payment_process_filter = "false";
            this.activeFilters.finance_payment_process_filter = "false";
            this.searchMode = false;
            
            this.get_all_items_delivered_invoices();
        },

        mounted: function() {
            this.handleResize();
            window.addEventListener("resize", this.handleResize);
            let that = this;
            
            const dateEl = document.getElementById('dates');
            if (dateEl) {
                this.datePickerInstance = new Lightpick({
                    field: dateEl,
                    singleDate: false,
                    repick: true,
                    startDate: this.searchForm.startDate ? moment(this.searchForm.startDate).toDate() : null,
                    endDate: this.searchForm.endDate ? moment(this.searchForm.endDate).toDate() : null,
                    onSelect: function(start, end){
                        that.searchForm.startDate = start ? start.startOf('day').format('YYYY-MM-DD') : '';
                        that.searchForm.endDate = end ? end.endOf('day').format('YYYY-MM-DD') : '';
                        
                        that.onFilterChange();
                        
                        const dateInput = document.getElementById('dates');
                        if (dateInput) {
                            if (start && end) {
                                dateInput.placeholder = `${start.format('YYYY-MM-DD')} - ${end.format('YYYY-MM-DD')}`;
                            } else if (start) {
                                dateInput.placeholder = start.format('YYYY-MM-DD');
                            } else {
                                dateInput.placeholder = 'Select Date Range';
                            }
                        }
                    }
                });
            }
        },

        beforeDestroy() {
            window.removeEventListener("resize", this.handleResize);
            this.stopLoadingAnimation(false);
            if (this.autoSaveTimeout) clearTimeout(this.autoSaveTimeout);
            if (this.filterDebounceTimer) clearTimeout(this.filterDebounceTimer);
            if (this.datePickerInstance) {
                this.datePickerInstance.destroy();
                this.datePickerInstance = null;
            }
        },
    }
</script>

<style scoped>
    /* ...unchanged styles from your original file... */
    .glass-theme {
        position: relative;
        background: transparent !important;
        min-height: 100vh;
    }

    .glass-theme::before {
        content: '';
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-image: url('https://m.media-amazon.com/images/I/81eSegKonhL._AC_SL1500_.jpg');
        background-size: cover;
        background-position: center;
        background-repeat: no-repeat;
        filter: blur(10px);
        opacity: 0.5;
        z-index: -2;
        transform: scale(1.1);
    }

    @keyframes fadeInPage {
        from { opacity: 0; transform: translateY(24px); filter: blur(2px); }
        to { opacity: 1; transform: translateY(0); filter: blur(0); }
    }

    .fade-in-page { animation: fadeInPage 0.7s cubic-bezier(0.2, 0.9, 0.4, 1.1); }

    @keyframes fadeInRow {
        from { opacity: 0; transform: translateX(-8px); }
        to { opacity: 1; transform: translateX(0); }
    }

    .fade-in-row {
        opacity: 0;
        animation: fadeInRow 0.28s cubic-bezier(0.2, 0.9, 0.4, 1) forwards;
    }

    .discrepancy-row {
        background: rgba(220, 53, 69, 0.25) !important;
        border-left: 4px solid #dc3545;
        box-shadow: inset 0 0 0 1px rgba(220, 53, 69, 0.5);
    }

    .discrepancy-row td {
        background: rgba(220, 53, 69, 0.15) !important;
        color: #2c3e50;
    }

    .glass-card {
        background: rgba(255, 255, 255, 0.85) !important;
        backdrop-filter: blur(10px) !important;
        border: 1px solid rgba(255, 255, 255, 0.3) !important;
        border-radius: 20px !important;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05) !important;
    }

    .glass-card >>> .card-body { padding: 20px; }

    .glass-alert {
        background: rgba(255, 255, 255, 0.85) !important;
        backdrop-filter: blur(10px) !important;
    }

    .glass-input {
        background: rgba(255, 255, 255, 0.9) !important;
        backdrop-filter: blur(5px) !important;
        border: 1px solid rgba(128, 128, 128, 0.3) !important;
        border-radius: 10px !important;
    }

    .glass-input:focus {
        border-color: rgba(54, 64, 80, 0.5) !important;
        box-shadow: 0 0 0 2px rgba(54, 64, 80, 0.1) !important;
    }

    .glass-select {
        background: rgba(255, 255, 255, 0.9) !important;
        backdrop-filter: blur(5px) !important;
        border: 1px solid rgba(128, 128, 128, 0.3) !important;
        border-radius: 10px !important;
        min-width: 140px;
    }

    .glass-btn-small {
        background: rgba(54, 64, 80, 0.85) !important;
        color: white !important;
        border: none !important;
        border-radius: 8px !important;
        padding: 4px 12px !important;
        transition: all 0.2s ease !important;
    }

    .glass-btn-small:hover {
        background: rgba(54, 64, 80, 1) !important;
        transform: translateY(-1px) !important;
    }

    .glass-btn-outline {
        background: rgba(255, 255, 255, 0.8) !important;
        backdrop-filter: blur(5px) !important;
        border: 1px solid rgba(54, 64, 80, 0.3) !important;
        transition: all 0.2s ease !important;
        border-radius: 10px !important;
    }

    .glass-btn-outline:hover {
        background: rgba(54, 64, 80, 0.1) !important;
        transform: translateY(-1px) !important;
    }

    .glass-datepicker-small >>> .mx-input {
        background: rgba(255, 255, 255, 0.9) !important;
        border: 1px solid rgba(128, 128, 128, 0.3) !important;
        border-radius: 8px !important;
        font-size: 11px !important;
        padding: 4px 8px !important;
        width: 180px;
    }

    .glass-table {
        background: rgba(255, 255, 255, 0.5) !important;
        border-radius: 12px !important;
        overflow: hidden;
    }

    .glass-table th {
        background: rgba(0, 0, 0, 0.05) !important;
        padding: 12px 8px !important;
        font-weight: 600 !important;
    }

    .glass-table td { padding: 10px 8px !important; }

    .sort-icon { margin-left: 5px; font-size: 11px; opacity: 0.7; }

    .customer-info { display: flex; flex-direction: column; gap: 2px; }
    .customer-phone { font-size: 12px; color: #64748b; }
    .customer-email { font-size: 11px; color: #94a3b8; }
    .cell-invoice { font-weight: 600; color: #1e293b; }

    .state-badge {
        font-size: 0.75rem;
        padding: 4px 8px;
        border-radius: 20px;
    }

    .delivery-date-badge {
        padding: 3px;
        text-align: center;
        background: rgba(255, 235, 59, 0.3);
        color: #000;
        border-radius: 6px;
        margin: 2px 0;
        font-size: 11px;
    }

    .filters-container {
        background: rgba(255, 255, 255, 0.6);
        backdrop-filter: blur(5px);
        border-radius: 16px;
        padding: 10px;
    }

    .filter-box {
        background: rgba(255, 255, 255, 0.7);
        border-radius: 12px;
        margin-left: 10px;
    }

    .glass-modal >>> .modal-content {
        background: rgba(255, 255, 255, 0.98) !important;
        backdrop-filter: blur(20px) !important;
        border: 1px solid rgba(255, 255, 255, 0.5) !important;
        border-radius: 24px !important;
    }

    .loading-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.85);
        backdrop-filter: blur(20px);
        z-index: 2000;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .loading-overlay-enter-active { animation: fadeIn 0.3s ease; }
    .loading-overlay-leave-active { animation: fadeOut 0.3s ease; }

    .loading-card {
        background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(245, 245, 245, 0.95));
        border-radius: 24px;
        padding: 48px 56px;
        text-align: center;
        box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
        animation: cardPop 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55);
        max-width: 450px;
        width: 90%;
    }

    @keyframes cardPop {
        from { opacity: 0; transform: scale(0.8); }
        to { opacity: 1; transform: scale(1); }
    }

    .loading-animation { margin-bottom: 24px; flex-shrink: 0; }

    .loading-ring {
        position: relative;
        width: 80px;
        height: 80px;
        margin: 0 auto;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .ring {
        position: absolute;
        width: 100%;
        height: 100%;
        border: 3px solid transparent;
        border-radius: 50%;
        animation: ringRotate 1.5s linear infinite;
    }

    .ring:nth-child(1) {
        border-top-color: #4299e1;
        border-left-color: #4299e1;
        animation-duration: 1s;
    }

    .ring:nth-child(2) {
        border-right-color: #48bb78;
        border-bottom-color: #48bb78;
        width: 80%;
        height: 80%;
        top: 10%;
        left: 10%;
        animation-duration: 1.2s;
        animation-direction: reverse;
    }

    .ring:nth-child(3) {
        border-left-color: #ed8936;
        border-bottom-color: #ed8936;
        width: 60%;
        height: 60%;
        top: 20%;
        left: 20%;
        animation-duration: 0.8s;
    }

    @keyframes ringRotate {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
    }

    .loading-logo {
        font-size: 28px;
        font-weight: bold;
        animation: logoPulse 1.5s ease-in-out infinite;
        z-index: 1;
    }

    .loading-logo i {
        font-size: 32px;
        background: linear-gradient(135deg, #4299e1, #48bb78);
        -webkit-background-clip: text;
        background-clip: text;
        color: transparent;
    }

    @keyframes logoPulse {
        0%, 100% { transform: scale(1); opacity: 0.7; }
        50% { transform: scale(1.1); opacity: 1; }
    }

    .loading-text { margin-bottom: 20px; flex-shrink: 0; }

    .loading-text h3 {
        color: #2d3748;
        font-size: 1.2rem;
        margin-bottom: 6px;
        display: inline-block;
    }

    .loading-dots { display: inline-flex; margin-left: 8px; }

    .loading-dots .dot {
        width: 5px;
        height: 5px;
        background: #4299e1;
        border-radius: 50%;
        margin: 0 2px;
        animation: dotBounce 1.4s ease-in-out infinite;
    }

    .loading-dots .dot:nth-child(1) { animation-delay: 0s; }
    .loading-dots .dot:nth-child(2) { animation-delay: 0.2s; }
    .loading-dots .dot:nth-child(3) { animation-delay: 0.4s; }

    @keyframes dotBounce {
        0%, 60%, 100% { transform: translateY(0); }
        30% { transform: translateY(-6px); }
    }

    .loading-progress {
        width: 80%;
        height: 3px;
        background: #e2e8f0;
        border-radius: 4px;
        overflow: hidden;
        margin: 0 auto 20px auto;
        flex-shrink: 0;
    }

    .progress-bar {
        height: 100%;
        background: linear-gradient(90deg, #4299e1, #48bb78, #ed8936, #4299e1);
        background-size: 200% 100%;
        animation: progressShine 1.5s linear infinite;
        transition: width 0.3s ease;
        border-radius: 4px;
    }

    @keyframes progressShine {
        0% { background-position: 200% 0; }
        100% { background-position: -200% 0; }
    }

    .loading-tips {
        margin-top: 10px;
        flex-shrink: 0;
        min-height: 65px;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .tip-text {
        color: #718096;
        font-size: 0.85rem;
        animation: tipFade 0.5s ease;
        margin: 0;
        padding: 0 15px;
        line-height: 1.4;
    }

    @keyframes tipFade {
        from { opacity: 0; transform: translateY(8px); }
        to { opacity: 1; transform: translateY(0); }
    }

    @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
    @keyframes fadeOut { from { opacity: 1; } to { opacity: 0; } }

    .ml-2 { margin-left: 0.5rem; }

    .non-header { font-weight: normal; font-size: 13px; }

    .ipt_tdhr {
        font-weight: normal;
        font-size: 13px;
        background: transparent;
        cursor: pointer;
        user-select: none;
        transition: background 0.2s;
    }

    .ipt_tdhr:hover { background: rgba(255,255,255,0.2); }

    .ipt_tdr {
        font-weight: normal;
        font-size: 13px;
        background-color: none;
    }

    .gap-2 { gap: 0.5rem; }
    .d-flex { display: flex; }
    .flex-column { flex-direction: column; }
    .mt-1 { margin-top: 0.25rem; }
    .mt-2 { margin-top: 0.5rem; }
    .mb-2 { margin-bottom: 0.5rem; }
    .mr-1 { margin-right: 0.25rem; }
    .text-right { text-align: right; }
    .text-left { text-align: left; }

    @media (max-width: 768px) {
        .glass-table { font-size: 11px; }
        .glass-table th, .glass-table td { padding: 6px 4px !important; }
        .loading-card { width: 320px; min-height: 380px; padding: 30px 20px; }
        .loading-tips { min-height: 55px; }
        .tip-text { font-size: 0.75rem; }
        .glass-select { min-width: 100px; font-size: 11px; }
    }
</style>


// WEBPACK FOOTER //
// src/components/AdminTools/AdminToolsDeliveredOrders.vue